import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { useIsMobileShellEnabled } from "@/react/components/Mobile/hooks/useIsMobileShellEnabled";

import type { AiView } from "./intents/urlParams";
import { useCircleAiStore } from "./useCircleAiStore";

interface OpenOptions {
  panelState?: AiView;
}

interface OpenConversationOptions extends OpenOptions {
  projectId?: string | null;
  title?: string | null;
}

/**
 * The canonical way to open CircleAI from inside the app. Call sites used to
 * re-derive the presentation fork individually; it lives here instead.
 *
 * Desktop drives the conversation panel directly. Mobile has no panel, so it
 * navigates to `/ai*` — the one surface where that route survives.
 *
 * Omit `panelState` to leave the current width alone.
 */
export const useOpenCircleAi = () => {
  const navigate = useNavigate();
  const isMobileShellEnabled = useIsMobileShellEnabled();

  const openNewChat = useCallback(
    ({ panelState }: OpenOptions = {}) => {
      if (isMobileShellEnabled) {
        // Reset the thread first so the route renders a blank one even when
        // the user is already on /ai.
        useCircleAiStore.getState().startNewChat({ openPanel: false });
        void navigate({ to: "/ai" });
        return;
      }
      useCircleAiStore.getState().startNewChat({ panelState });
    },
    [isMobileShellEnabled, navigate],
  );

  // `startChatWithPrompt` starts the chat itself, so this must not also call
  // `openNewChat`.
  const openNewChatWithPrompt = useCallback(
    (prompt: string, panelState?: AiView) => {
      if (isMobileShellEnabled) {
        useCircleAiStore
          .getState()
          .startChatWithPrompt(prompt, { openPanel: false });
        void navigate({ to: "/ai" });
        return;
      }
      useCircleAiStore.getState().startChatWithPrompt(prompt, { panelState });
    },
    [isMobileShellEnabled, navigate],
  );

  const openConversation = useCallback(
    (
      uuid: string,
      {
        panelState,
        projectId = null,
        title = null,
      }: OpenConversationOptions = {},
    ) => {
      if (isMobileShellEnabled) {
        void navigate({
          to: "/ai/$conversationId",
          params: { conversationId: uuid },
        });
        return;
      }
      useCircleAiStore
        .getState()
        .openConversation(uuid, title, projectId, { panelState });
    },
    [isMobileShellEnabled, navigate],
  );

  return {
    openConversation,
    openNewChat,
    openNewChatWithPrompt,
    surface: isMobileShellEnabled ? ("page" as const) : ("panel" as const),
  };
};
